import { useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'

import { Employee } from 'modules/core/hooks/useCurrentSession'

import { isExpensableReporter, isOwnExpensable } from 'modules/expenses/lib/expensables/expensable'
import { Expensable } from 'modules/expenses/types/Expensable'

export interface PermissionsProps {
  currentEmployee: Employee
  expensable?: Expensable
  asReviewer: boolean
  canEditExpensesWithPendingStatus: boolean
}

export interface WithFieldPermissionsProps extends PermissionsProps {
  isFieldReadOnly?: boolean
  isFieldMandatory?: boolean
}

export const isFieldEditable = (props: PermissionsProps & { fieldValue?: unknown }): boolean => {
  const { expensable, asReviewer, canEditExpensesWithPendingStatus, currentEmployee } = props

  if (!expensable) {
    if (asReviewer) {
      throw new Error('Expensable is required for reviewer')
    } else {
      return true
    }
  }
  const isOwn = isOwnExpensable(expensable, currentEmployee)
  const isReporter = isExpensableReporter(expensable, currentEmployee)

  switch (expensable.status) {
    case 'draft':
      return isOwn || isReporter
    case 'pending':
      return canEditExpensesWithPendingStatus
    case 'changes_requested':
      return isOwn
    default:
      return false
  }
}

const withFieldPermissions = <P extends WithFieldPermissionsProps>(
  WrappedComponent: React.ComponentType<P>,
  fieldName: string
) => {
  const WithFieldPermissions = (props: P & PermissionsProps) => {
    const { getValues } = useFormContext()
    const initialFieldValueRef = useRef<unknown>()

    // Capture the initial field value only once when the component mounts
    useEffect(() => {
      if (initialFieldValueRef.current === undefined) {
        initialFieldValueRef.current = getValues(fieldName)
      }
    }, [getValues])

    const fieldValue = initialFieldValueRef.current

    const isFieldReadOnly =
      props.isFieldReadOnly ??
      !isFieldEditable({
        ...props,
        fieldValue,
        isFieldMandatory: props.isFieldMandatory,
      })

    return <WrappedComponent {...props} isFieldReadOnly={isFieldReadOnly} />
  }

  return WithFieldPermissions
}

export default withFieldPermissions
